import React, { useCallback, useEffect, useRef, useState } from 'react';
import css from './FeaturedListings.module.css';
import classNames from 'classnames';
import { useIntl } from 'react-intl';
import { formatMoney } from '../../util/currency';
import { createSlug } from '../../util/urlHelpers';
import { NamedLink, ResponsiveImage, FavoriteButton } from '../../components';
import { formatLabel } from '../../util/data';

const FeaturedListings = props => {
  const { listings } = props;
  const trackRef = useRef(null);
  const cardWidthRef = useRef(null);
  const [showNav, setShowNav] = useState(false);
  const intl = useIntl();

  const updateNavVisibility = useCallback(() => {
    const node = trackRef.current;
    if (!node) return;
    setShowNav(node.scrollWidth > node.clientWidth + 1);
  }, []);

  useEffect(() => {
    // Defer nav visibility check to after first paint to avoid blocking main thread
    const rafId = typeof window.requestAnimationFrame === 'function'
      ? window.requestAnimationFrame(() => {
          updateNavVisibility();
        })
      : null;

    window.addEventListener('resize', updateNavVisibility);

    return () => {
      if (rafId && typeof window.cancelAnimationFrame === 'function') {
        window.cancelAnimationFrame(rafId);
      }
      // Reset cached card width on unmount
      cardWidthRef.current = null;
      window.removeEventListener('resize', updateNavVisibility);
    };
  }, [listings, updateNavVisibility]);

  if (!listings || listings.length === 0) {
    return null;
  }

  const scrollByCards = direction => {
    const node = trackRef.current;
    if (!node) return;
    // Use cached card width to avoid forced reflow on every click
    if (!cardWidthRef.current) {
      const card = node.querySelector(`.${css.card}`);
      cardWidthRef.current = card ? card.getBoundingClientRect().width : 300;
    }
    const gap = 24;
    node.scrollBy({ left: (cardWidthRef.current + gap) * direction, behavior: 'smooth' });
  };

  const getFormattedPrice = price => {
    const contactForPrice = 'Contact for price.';
    const amount = Number(price?.amount);

    if (!price || !Number.isFinite(amount) || amount <= 0) {
      return contactForPrice;
    }

    try {
      return formatMoney(intl, price) || contactForPrice;
    } catch {
      return contactForPrice;
    }
  };

  // Extract brand and dimensions from listing data
  const getListingInfo = listing => {
    const { publicData } = listing.attributes;
    const brandName =
      listing.author?.attributes?.profile?.displayName || 'Unknown Brand';

    // Extract dimensions from publicData
    const getDimensions = () => {
      const { beam_length, beam_width } = publicData || {};
      if (beam_length && beam_width) {
        const length = beam_length.replace('_inch', '"').replace('_inch', '"');
        const width = beam_width.replace('_inch', '"').replace('_inch', '"');
        return `${length}W x ${width}H`;
      }
      return null;
    };

    return {
      brand: brandName,
      dimensions: getDimensions(),
    };
  };

  return (
    <section className={css.featuredListings}>
      <div className={css.header}>
        <h2 className={css.title}>Featured Listings</h2>
        <p className={css.subtitle}>
          Check out our hand-picked selection of top quality items.
        </p>
      </div>

      <div className={css.slider}>
        {showNav ? (
          <button
            aria-label="Previous"
            className={css.navBtn}
            onClick={() => scrollByCards(-1)}
          >
            ‹
          </button>
        ) : (
          <div className={css.navSpacer} aria-hidden="true" />
        )}
        <div className={css.track} ref={trackRef}>
          {listings.map(l => {
            const { title, price, publicData } = l.attributes;
            const formattedPrice = getFormattedPrice(price);
            const { categoryLevel2, width,height,pub_stock,serviceLocations } = publicData || {};
            const getListingInfo = listing => {
              const pd = listing || {};
              const { width,height} = pd;
              const rackType = categoryLevel2
              const formattedRackType = formatLabel(rackType)
              const findFieldValue = key => {
                const fieldArrays = [
                  publicData?.requiredFieldsArray,
                  publicData?.optionalFieldsArray,
                ].filter(Array.isArray);
                const field = fieldArrays.flat().find(item => item?.key === key);
                return field?.value ?? publicData?.[key] ?? publicData?.[`pub_${key}`] ?? null;
              };
              const manufacturer =
                findFieldValue('manufacturer') ||
                findFieldValue('brand') ||
                findFieldValue('frame_manufacturer') ||
                findFieldValue('beam_manufacturer');
              const manufacturerDisplay =
                `${manufacturer || ''}`.toLowerCase().includes('other')
                  ? publicData?.pub_manufacturer_other || manufacturer
                  : manufacturer;
              const formattedManufacturer = manufacturerDisplay
                ? formatLabel(String(manufacturerDisplay))
                : null;
          
              // const dimensions = (() => {
              //   if (width && height) {
              //     const parseDimension = value => String(value).split('_inch')[0];
              //     const formattedWidth = `${parseDimension(width)}W`;
              //     const formattedHeight = `${parseDimension(height)}H`;
              //     return `${formattedWidth} x ${formattedHeight}`;
              //   }
              //   return null;
              // })();

              const dimensions = (() => {
                const arr = publicData?.requiredFieldsArray || [];
                const findByKey = (key) =>
                  Array.isArray(arr) ? arr.find((f) => (f?.key || '').toLowerCase() === key.toLowerCase()) : null;
                const widthField = findByKey('width');
                const heightField = findByKey('height');
                const parseDimension = (value) => String(value).split('_inch')[0];
                const widthVal = widthField?.value;
                const heightVal = heightField?.value;
                const formattedWidth = widthVal ? `${parseDimension(widthVal)}W` : null;
                const formattedHeight = heightVal ? `${parseDimension(heightVal)}"H` : null;
                const parts = [formattedWidth, formattedHeight].filter(Boolean);
                return parts.length > 0 ? parts.join(' x ') : null;
              })();
          
              return { formattedManufacturer, formattedRackType, dimensions };
            };
          
            const { formattedManufacturer, formattedRackType, dimensions } = getListingInfo(publicData);
            const locationData = serviceLocations && serviceLocations[0] && serviceLocations[0].location?.search;

            const image = l.images && l.images.length > 0 ? l.images[0] : null;
            const isImageString = typeof image === 'string';
            const responsiveImageProps = isImageString
              ? { url: image, variants: [] }
              : { image: image, variants: ['listing-card'] };

            const { availability, preSaleDate } = publicData || {};
            const isPreSale = availability === 'pre-sale';
            let availabilityLabel = null;
            if (isPreSale && preSaleDate) {
              const date = new Date(Number(preSaleDate) * 1000);
              const month = date.toLocaleString('default', { month: 'long' });
              const year = date.getFullYear();
              availabilityLabel = `AVAILABLE ${month} ${year}`.toUpperCase();
            }

            return (
              <NamedLink
                name="ListingPage"
                params={{ id: l.id.uuid, slug: createSlug(title) }}
                key={l.id.uuid}
                className={css.cardLink}
              >
                <article className={isPreSale ? css.precard : css.card}>
                  <div className={css.imageWrap}>
                    {isPreSale ? (
                      <span className={css.tag}>Pre-Sale</span>
                    ) : null}
                    {image ? (
                      <ResponsiveImage
                        alt={title || 'Featured listing'}
                        className={css.image}
                        width="540"
                        height="309"
                        loading="lazy"
                        {...responsiveImageProps}
                      />
                    ) : (
                      <div className={css.fallbackImage}>
                        <img
                          src="/static/images/pallet-default.png"
                          alt="No image available"
                          width="540"
                          height="309"
                          loading="lazy"
                        />
                      </div>
                    )}
                    <div className={css.favoriteButtonWrapper}>
                      <FavoriteButton listingId={l.id} />
                    </div>
                  </div>
                  <div className={css.cardBody}>
                    <p className={css.price}>{formattedPrice}</p>
                    <h3 className={css.cardTitle}>{title}</h3>
                    <div className={css.brandWrapper}>
                      {formattedManufacturer && <p className={css.brand}>{formattedManufacturer}</p>}
                      {formattedManufacturer && formattedRackType && (
                        <p className={css.dimensions}>{'|'}</p>
                      )}
                      {formattedRackType && <p className={css.brand}>TYP: {formattedRackType}</p>}
                      {dimensions && (
                        <>
                          <p className={css.dimensions}>{'|'} {dimensions}</p>
                        </>
                      )}
                    </div>
                    {pub_stock ? <p className={css.cardMetaStock}>QTY: {pub_stock}</p> : <p className={css.cardMetaStock}></p>}
                    {locationData ? <p className={css.cardMetaStock}>LOC: {locationData}</p> : <p className={css.cardMetaStock}></p>}
                    {availabilityLabel ? (
                      <p className={css.availability}>{availabilityLabel}</p>
                    ) : <div className={css.availabilityEmpty}></div>}
                  </div>
                </article>
              </NamedLink>
            );
          })}
        </div>
        {showNav ? (
          <button
            aria-label="Next"
            className={css.navBtn}
            onClick={() => scrollByCards(1)}
          >
            ›
          </button>
        ) : (
          <div className={css.navSpacer} aria-hidden="true" />
        )}
      </div>
    </section>
  );
};

export default FeaturedListings;
